/* eslint-disable  @typescript-eslint/no-explicit-any */

import { VodRowViewProps } from '@/types/vod/vodRowType'
import {
    catType,
    cutvType,
    externalType,
    vodDetailsRequestKeyword,
    vodItemDetailsAdverts,
    vodSeriesDetailsRequestKeyword,
    vodType,
    webType,
} from '@/shared/constants/vodConstants'
import { VodItemProps } from '@/types/vod/VodItem'
import {
    getErrorMessage,
    getEtagFromLocalStorage,
    setEtagToLocalStorage,
} from '@/shared/lib'
import { AdvertPayload } from '@/types/vod/vodItemAdvert'
import { getWithAuth, postWithAuth } from '@/api/httpRequests'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import {
    getFromLocalStorage,
    removeFromLocalStorage,
} from '@/shared/localStorage/localStorageRepository'
import {
    categoryResponseTypeStreamIQ,
    IVODServiceStreamIQ,
    ListItem,
    NewVodData,
} from '@/shared/types/services/types/vodService/IVodServiceStreamIQ'
import {
    getBootstraps,
    mapNewVodDataToVodItemProps,
} from '@/clients/streamiq/shared/lib'
import { LAST_ACTUAL_PROFILE } from '@/shared/constants'
import { EpisodeItemInterface, Season } from '@/types/vod/VodItemDetails'
import {
    EPG_SERVICE_NAME,
    EPISODES_TYPE,
    MOVIE_EVENT_TYPE,
    MOVIE_TYPE,
    playbackPlaceholder,
    RECOMMENDATION_TYPE,
    SHOW_TYPE,
    VOD_SERVICE_NAME,
} from '@/clients/streamiq/shared/constantsStreamIq'
import { Bookmark } from '@/clients/streamiq/types/vodStreamIQTypes'

// Type for the actual API response structure
export interface ApiListItem {
    id: string
    listId: string
    reference: {
        type: string
        serviceName: string
        id: string
    }
    ordinal: number
    serviceName: string
    type: string
}

export interface SourceSeason {
    showId: string
    description: string
    id: string
    releaseDate: string
    tags: string[]
    seasonNumber: number
    serviceName: string
    type: string
}

export interface Reference {
    id: string
    type: 'movie' | 'show' | 'channel' | 'list' | 'other' | 'customResource'
    serviceName: 'vod-service' | 'epg-service' | 'layout-service' | 'other'
}

export interface ListItems {
    id: string
    listId: string
    reference: Reference
    ordinal: number
    serviceName: string // e.g., "layout-service"
    type: 'listItem'
}

class VODServiceStreamIQ implements IVODServiceStreamIQ {
    mapToVodRowViewProps = (
        data: categoryResponseTypeStreamIQ
    ): VodRowViewProps => {
        let parsedCustomVariable: any = null

        try {
            parsedCustomVariable =
                data.customVariable && typeof data.customVariable === 'string'
                    ? JSON.parse(data.customVariable)
                    : null
        } catch (error) {
            console.error('Error parsing customVariable:', error)
        }

        return {
            atmosphericUrl: '',
            productId: data.id,
            title: data.name,
            handleGoToCategoryButtonClick: (itemId, title, type) => {
                console.log(
                    `Navigating to category: ${title} with type ${type} and item id ${itemId}`
                )
            },
            isSkeletons: false,
            maxHeight: 200,
            rowHeightPercentage: parsedCustomVariable?.rowHeightPercentage
                ? parsedCustomVariable?.rowHeightPercentage
                : 100,
            cardRadiusDp: parsedCustomVariable?.cardRadiusDp
                ? parsedCustomVariable?.cardRadiusDp
                : 8,
            hideTitle: parsedCustomVariable?.hideTitle
                ? parsedCustomVariable?.hideTitle
                : false,
            hideCaptions: parsedCustomVariable?.hideCaptions
                ? parsedCustomVariable?.hideCaptions
                : false,
            hideProgress: parsedCustomVariable?.hideProgress
                ? parsedCustomVariable?.hideProgress
                : false,
            isHomeContentRow: true,
            query: data.query,
            scrollPosition: {
                x: 0,
                y: 0,
            },
            isCardOrientationPriorityHorizontal:
                parsedCustomVariable?.isCardOrientationPriorityHorizontal
                    ? parsedCustomVariable?.isCardOrientationPriorityHorizontal
                    : false,
        }
    }

    mapRecommendationsToVodRowViewProps = (data: any): VodRowViewProps => {
        const landscapeImage = data.sourceImages?.find(
            (img: any) =>
                img.orientation === 'landscape' && img.size === 'large'
        )?.url
        return {
            productId: data.id,
            title: data.title,
            handleGoToCategoryButtonClick: (
                itemId: string,
                title: string,
                type: string
            ) => {
                console.log(
                    `Navigating to category: ${title} with type ${type} and item id ${itemId}`
                )
            },
            maxHeight: 200,
            atmosphericUrl: landscapeImage || '',
            isSkeletons: false,
            rowHeightPercentage: 100,
            cardRadiusDp: 8,
            hideTitle: false,
            hideCaptions: false,
            hideProgress: false,
            isHomeContentRow: true,
            serviceName: data.serviceName,
            query: data.query || '',
            scrollPosition: {
                x: 0,
                y: 0,
            },
        }
    }

    mapToSeason(data: any): Season {
        return {
            id: data.id,
            productId: data.showId,
            productType: data.type,
            title: data.description ?? 'Untitled Season',
            atmosphericUrl: '',
            seasonNo: data.seasonNumber,
            numberOfEpisodes: 0,
            tags: data.tags ?? [],
        }
    }

    mapToEpisodeItem(data: any): EpisodeItemInterface {
        return {
            title: data.title,
            productId: data.id,
            productType: data.type,
            imageUrl:
                data.sourceImages?.find(
                    (img: any) =>
                        img.size === 'large' && img.orientation === 'landscape'
                )?.url || '',
            aspectRatio: '16:9',
            duration: data.runtime || 0,
            position: data.episodeNumber || 0,
            label: {
                imageUrl: data.sourceImages?.[0]?.url || '',
                text: data.title || '',
                textColor: '',
                backgroundColor: '',
            },
            description: data.description,
            year: new Date(data.releaseDate).getFullYear().toString(),
            price: '', // Assuming price is not present in the data provided, set as needed
            cast:
                data.credits
                    ?.filter((credit: any) => credit.role === 'Actor')
                    .map((actor: any) => actor.name) || [],
            pcLevel: 0, // Assuming pcLevel is not present, set as needed
            atmosphericUrl:
                data.sourceImages?.find(
                    (img: any) =>
                        img.size === 'large' && img.orientation === 'landscape'
                )?.url || '',
            country: data.countryOfOrigin || '',
            isPurchased: 0, // Assuming isPurchased field not present in data, set as needed
            entitlementEndInfo: '', // Assuming entitlementEndInfo is not present in the data provided
            additionalRating:
                data.parentalRatings?.map((rating: any) => ({
                    boxColor: '',
                    textColor: rating.system,
                    symbols: [rating.rating],
                })) || [],
            serviceName: data.serviceName,
        }
    }

    getVodServiceId = async (alias: string) => {
        const bootstraps = getBootstraps()
        const listGroupURL = bootstraps?.services?.['layout-service']?.listGroup

        if (!listGroupURL) {
            console.error('No listGroup URL found in bootstrap configuration')
            throw new Error(
                'ListGroup URL not found in bootstrap configuration'
            )
        }

        const res = await getWithAuth(listGroupURL, false)

        const { response } = res || {}
        if (!response || !response.resources) {
            throw new Error('Invalid response from listGroup endpoint')
        }

        const foundItem = response.resources.find(
            (item: any) => item.name === alias
        )
        if (!foundItem) {
            throw new Error(`No list group found with alias: ${alias}`)
        }

        return foundItem.id
    }

    getRows = async (
        alias: string,
        queryParams?: Record<string, string | number | boolean>
    ): Promise<VodRowViewProps[]> => {
        const bootstraps = getBootstraps()
        const listsURL = bootstraps?.services?.['layout-service']?.list
        // const listsURL = bootstraps?.services?.['layout-service']?.listGroup

        const moviesCategoryID = await this.getVodServiceId(alias)

        const etag = getEtagFromLocalStorage(moviesCategoryID)

        if (etag && etag.requestTime) {
            const isValidEtag = Date.now() < Number(etag.requestTime)

            if (isValidEtag) return []
        }

        const res = await getWithAuth(
            `${listsURL}?listGroupId=145fba27-7e52-40dd-8d2a-71c0024c68fa`,
            false,
            queryParams
        )

        if (etag?.eTag === res?.eTag) return []

        removeFromLocalStorage(moviesCategoryID.toString())

        const { response, maxAge, eTag, requestTime } = res || {}

        if (maxAge && eTag && requestTime) {
            setEtagToLocalStorage(moviesCategoryID, maxAge, requestTime, eTag)
        }

        return response?.resources.map((item: categoryResponseTypeStreamIQ) =>
            this.mapToVodRowViewProps(item)
        )
    }

    getAdverts = async (
        item: string,
        itemId: number
    ): Promise<AdvertPayload> => {
        const requestBody = JSON.stringify({
            item,
            itemId,
        })
        return await postWithAuth(vodItemDetailsAdverts, true, requestBody)
    }

    getItems = async (
        id: string,
        url: string,
        queryParams?: Record<string, string | number | boolean>
    ): Promise<any> => {
        const bootstraps = getBootstraps()

        const profileId = getFromLocalStorage(LAST_ACTUAL_PROFILE)

        // Use the dynamic link from bootstrap if no URL is provided
        const listItemURL =
            url || bootstraps?.services?.['layout-service']?.listItem

        if (!listItemURL) {
            console.error('No listItem URL found in bootstrap configuration')
            return []
        }

        const etag = getEtagFromLocalStorage(id)

        // // Construct the URL with proper parameters
        // const baseUrl = url || `${listItemURL}?listId=${id}`
        // const finalUrl = url ? `${baseUrl}&` : baseUrl

        const res = await getWithAuth(url, false, undefined, {
            'X-Stream-Iq-Profile-Id': profileId,
        })

        // Check if ETag matches (no changes)
        if (etag && etag.eTag === res?.eTag) {
            return []
        }

        removeFromLocalStorage(id.toString())

        if (!res) return []

        const { response, maxAge, eTag: responseETag, requestTime } = res

        if (maxAge && responseETag && requestTime) {
            setEtagToLocalStorage(id, maxAge, requestTime, responseETag)
        }
        if (response && response.resources) {
            return response.resources
        }

        return []
    }

    getPlaybacks = async (
        productId?: string,
        productType?: string,
        serviceName?: string
    ) => {
        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['session-service']?.session

        if (!url) {
            console.error('No session URL found in bootstrap configuration')
            return playbackPlaceholder
        }

        const actualProfile = getFromLocalStorage(LAST_ACTUAL_PROFILE)
        try {
            const response = await postWithAuth(
                url,
                false,
                JSON.stringify({
                    streamingProtocol: 'hls',
                    profileId: actualProfile,
                    deviceFingerprint: '',
                    reference: {
                        id: productId || '',
                        type: productType,
                        serviceName,
                    },
                })
            )

            return {
                ...playbackPlaceholder,
                uri: response?.playInfo?.url || '',
            }
        } catch (error) {
            console.log(error)
        }
        return playbackPlaceholder
    }

    getPlaybacksPosition = async (): Promise<Bookmark[]> => {
        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['session-service']?.bookmark

        if (!url) {
            console.error('No bookmark URL found in bootstrap configuration')
            return []
        }

        const actualProfile = getFromLocalStorage(LAST_ACTUAL_PROFILE)
        const res = await getWithAuth(
            `${url}?profileId=${actualProfile}&`,
            false
        )

        if (!res) return []

        const { response } = res

        return response.resources
    }

    getVodDetails = async (
        productId: string,
        assetType: string,
        profileId?: string
    ) => {
        const requestBody = JSON.stringify({
            productId,
            productType: assetType,
        })

        const response = await postWithAuth(
            vodDetailsRequestKeyword,
            true,
            requestBody,
            profileId
        )

        return response.details
    }

    fetchSingleVodMovieItem = async (id: string): Promise<NewVodData> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL = bootstraps?.services?.['vod-service']?.movie

            if (!movieURL) {
                console.error('No movie URL found in bootstrap configuration')
                return {} as NewVodData
            }

            const res = await getWithAuth(`${movieURL}/${id}`, false)

            if (!res) return {} as NewVodData

            const { response } = res

            return response
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
        }
        return {} as NewVodData
    }

    fetchSingleEPGMovieEventItem = async (id: string): Promise<NewVodData> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL = bootstraps?.services?.['epg-service']?.movieEvent

            if (!movieURL) {
                console.error(
                    'No movieEvent URL found in bootstrap configuration'
                )
                return {} as NewVodData
            }

            const res = await getWithAuth(`${movieURL}/${id}`, false)

            if (!res) return {} as NewVodData

            const { response } = res

            return response
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
        }
        return {} as NewVodData
    }

    fetchSingleEPGShowItem = async (id: string): Promise<NewVodData> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL = bootstraps?.services?.['epg-service']?.show

            if (!movieURL) {
                console.error('No show URL found in bootstrap configuration')
                return {} as NewVodData
            }

            const res = await getWithAuth(`${movieURL}/${id}`, false)

            if (!res) return {} as NewVodData

            const { response } = res

            return response
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
            return {} as NewVodData
        }
    }

    fetchSingleVodShowItem = async (id: string): Promise<NewVodData> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL = bootstraps?.services?.['vod-service']?.show

            if (!movieURL) {
                console.error('No show URL found in bootstrap configuration')
                return {} as NewVodData
            }

            const res = await getWithAuth(`${movieURL}/${id}`, false)

            if (!res) return {} as NewVodData

            const { response: show } = res

            const seasons = await this.fetchSeasons(id)
            const mappedSeason = seasons.map((season) =>
                this.mapToSeason(season)
            )

            show.seasons = mappedSeason
            return show
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
            return {} as NewVodData
        }
    }

    fetchSingleVodRecommendationItem = async (
        id: string
    ): Promise<NewVodData> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL =
                bootstraps?.services?.['recommendation-service']?.recommendation

            if (!movieURL) {
                console.error(
                    'No recommendation URL found in bootstrap configuration'
                )
                return {} as NewVodData
            }

            const res = await getWithAuth(`${movieURL}/${id}`, false)

            if (!res) return {} as NewVodData

            const { response } = res

            return response
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
            return {} as NewVodData
        }
    }

    fetchSingleVodEpisodeItem = async (id: string): Promise<NewVodData> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL = bootstraps?.services?.['vod-service']?.episode

            if (!movieURL) {
                console.error('No episode URL found in bootstrap configuration')
                return {} as NewVodData
            }

            const res = await getWithAuth(`${movieURL}/${id}`, false)

            if (!res) return {} as NewVodData

            const { response } = res

            return response
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
            return {} as NewVodData
        }
    }

    fetchSeasons = async (id: string): Promise<SourceSeason[]> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL = bootstraps?.services?.['vod-service']?.season

            if (!movieURL) {
                console.error('No season URL found in bootstrap configuration')
                return []
            }

            const res = await getWithAuth(`${movieURL}?showId=${id}`, false)

            if (!res) return []

            const { response } = res

            return response.resources.sort(
                (itemA: SourceSeason, itemB: SourceSeason) =>
                    itemA.seasonNumber - itemB.seasonNumber
            )
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
            return [] as SourceSeason[]
        }
    }

    fetchRecommendationList = async (
        type: string,
        resourceId: string,
        serviceName: string
    ): Promise<any> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL =
                bootstraps?.services?.['recommendation-service']?.recommendation

            if (!movieURL) {
                console.error(
                    'No recommendation URL found in bootstrap configuration'
                )
                return []
            }

            const query = {
                recommendationType: 'moreLikeThis',
                seedServiceName: serviceName,
                seedResourceType: type,
                seedResourceId: resourceId,
                outputServiceName: '',
            }

            const res = await getWithAuth(`${movieURL}?`, false, query)

            if (!res) return []

            const { response } = res

            const fetchRecommendationItems = await Promise.allSettled(
                response.resources.map(async (item: any) =>
                    this.fetchVodItemDetails(
                        item.reference.id,
                        item.reference.type,
                        item.reference.serviceName
                    )
                )
            )
            const recommendationItems = fetchRecommendationItems
                .filter((result) => result.status === 'fulfilled')
                .map((result: any) => result.value)
                .filter((item) => Boolean(item))

            return recommendationItems.map(
                (item: any) => new VodItem(mapNewVodDataToVodItemProps(item))
            )
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
            return [] as SourceSeason[]
        }
    }

    fetchVodEpisodes = async (
        showId: string,
        seasonId: string
    ): Promise<EpisodeItemInterface[]> => {
        try {
            const bootstraps = getBootstraps()

            const movieURL = bootstraps?.services?.['vod-service']?.episode

            if (!movieURL) {
                console.error('No episode URL found in bootstrap configuration')
                return []
            }

            const res = await getWithAuth(
                `${movieURL}?showId=${showId}&seasonId=${seasonId}`,
                false
            )

            if (!res) return []

            const { response } = res

            return response.resources.map((item: any) =>
                this.mapToEpisodeItem(item)
            )
        } catch (error) {
            getErrorMessage('Error fetching single VOD item:', error)
            return []
        }
    }

    getVodSeriesDetails = async (
        productId: string,
        assetType: string,
        profileId?: string
    ) => {
        const requestBody = JSON.stringify({
            productId,
            productType: assetType,
        })

        const response = await postWithAuth(
            vodSeriesDetailsRequestKeyword,
            true,
            requestBody,
            profileId
        )

        return response.details
    }

    private fetchDetailedVodItems = async (
        apiRowItems: ListItems[]
    ): Promise<VodItem[]> => {
        const detailedItems = await Promise.allSettled(
            apiRowItems.map(async (item) => {
                const { id, type, serviceName } = item.reference
                return this.fetchVodItemDetails(id, type, serviceName)
            })
        )

        const validItems = detailedItems
            .filter(
                (result): result is PromiseFulfilledResult<any> =>
                    result.status === 'fulfilled'
            )
            .map((result) => result.value)
            .filter(Boolean)

        const mappedItems = validItems.map(
            (item) => new VodItem(mapNewVodDataToVodItemProps(item))
        )
        return mappedItems
    }

    getVodItems = async (
        id: string,
        url: string,
        queryParams?: Record<string, string | number | boolean>,
        isCardOrientationPriorityHorizontal?: boolean
    ): Promise<VodItem[]> => {
        try {
            const apiRowItems = await this.getItems(id, url)

            if (!apiRowItems.length) return []

            const detailedVodItems = await this.fetchDetailedVodItems(
                apiRowItems
            )
            return detailedVodItems
        } catch (error) {
            getErrorMessage('Error getting VOD items:', error)
            return []
        }
    }

    fetchVodItemsFromList = async (
        listItems: ApiListItem[],
        isCardOrientationPriorityHorizontal?: boolean
    ): Promise<VodItem[]> => {
        try {
            if (!listItems || !listItems.length) return []

            // Fetch details for each item in parallel
            const fetchPromises = listItems.map(async (item: ApiListItem) => {
                try {
                    const { reference } = item
                    if (
                        !reference ||
                        !reference.id ||
                        !reference.type ||
                        !reference.serviceName
                    ) {
                        console.warn('Invalid reference in list item:', item)
                        return null
                    }

                    // Fetch the actual movie/show details
                    const vodDetails = await this.fetchVodItemDetails(
                        reference.id,
                        reference.type,
                        reference.serviceName
                    )

                    if (!vodDetails) {
                        console.warn(
                            'Failed to fetch details for item:',
                            reference.id
                        )
                        return null
                    }

                    // Convert to VodItem
                    const vodItemProps = mapNewVodDataToVodItemProps(
                        vodDetails,
                        Boolean(isCardOrientationPriorityHorizontal)
                    )

                    return new VodItem(vodItemProps)
                } catch (error) {
                    console.error('Error fetching item details:', error)
                    return null
                }
            })

            // Wait for all promises to resolve
            const results = await Promise.allSettled(fetchPromises)

            // Filter out failed requests and null results
            const vodItems = results
                .filter((result) => result.status === 'fulfilled')
                .map((result: any) => result.value)
                .filter((item) => item !== null)

            return vodItems
        } catch (error) {
            getErrorMessage('Error fetching VOD items from list:', error)
            return []
        }
    }

    createItemsObjects = (itemsList: VodItemProps[]): VodItem[] => {
        if (!itemsList || !itemsList.length) return []

        const validatedItems = itemsList.filter((apiItem: VodItemProps) => {
            if (apiItem.productType && apiItem.id) {
                const type = apiItem.productType
                if (type === webType || type === externalType) {
                    if (apiItem.externalUri) return apiItem
                } else {
                    if (type === vodType && apiItem.imageUrl && apiItem.title)
                        return apiItem
                    else if (type === catType && apiItem.title) {
                        return apiItem
                    } else if (type === cutvType) return apiItem
                }
            }
            return null
        })

        return validatedItems.map((item: VodItemProps) => new VodItem(item))
    }

    fetchRowsHome = async (): Promise<VodRowViewProps[] | undefined> => {
        try {
            const res = await this.getRows('home')

            return res
        } catch (error) {
            getErrorMessage('fetchRowsHome', error)
        }
    }

    fetchRowsSeriesScreen = async (): Promise<
        VodRowViewProps[] | undefined
    > => {
        try {
            return await this.getRows('series')
        } catch (error) {
            getErrorMessage('fetchRowsHome', error)
        }
    }

    fetchRowsVOD = async (
        queryParams?: Record<string, string | number | boolean>
    ): Promise<VodRowViewProps[] | undefined> => {
        try {
            return this.getRows('movies', queryParams)
        } catch (error) {
            getErrorMessage('fetchRowsVOD', error)
        }
    }

    fetchRowsSeries = async (): Promise<VodRowViewProps[] | undefined> => {
        try {
            return this.getRows('series')
        } catch (error) {
            getErrorMessage('fetchRowsSeries', error)
        }
    }

    createId = (id: number | string) => id.toString()

    fetchVodItemDetails = async (
        productId: string,
        assetType: string,
        serviceName?: string
    ): Promise<any> => {
        if (!productId || !assetType) {
            throw new Error('Invalid productId or assetType')
        }

        if (assetType === MOVIE_TYPE) {
            return await this.fetchSingleVodMovieItem(productId)
        }

        if (assetType === SHOW_TYPE && serviceName === EPG_SERVICE_NAME) {
            return await this.fetchSingleEPGShowItem(productId)
        }

        if (assetType === SHOW_TYPE && serviceName === VOD_SERVICE_NAME) {
            return await this.fetchSingleVodShowItem(productId)
        }

        if (
            assetType === MOVIE_EVENT_TYPE &&
            serviceName === EPG_SERVICE_NAME
        ) {
            return await this.fetchSingleEPGMovieEventItem(productId)
        }

        if (assetType === EPISODES_TYPE) {
            return await this.fetchSingleVodEpisodeItem(productId)
        }

        if (assetType === RECOMMENDATION_TYPE) {
            return await this.fetchSingleVodRecommendationItem(productId)
        }
        throw new Error(
            `Unsupported assetType or serviceName combination. Asset Type is:${assetType}. Service Name is: ${serviceName}`
        )
    }
}

export default new VODServiceStreamIQ()
